import type { GlyphInfo } from '@meitu/x-design-text';
import { unionBounds } from '@/utils/bounds-kit';
import type { Bounds } from '@/version-types';
import type { TextRenderInput, TextRenderSnapshot } from '../../types';
import { findLineIndex, transformBounds } from '../../utils';

export type ArtTextFragment = {
    spanIndex: number;
    visibleIndex: number;
    left: number;
    top: number;
    width: number;
    height: number;
    centerX: number;
    centerY: number;
    rotate: number;
    paintBounds: Bounds;
};

/**
 * 按 UTF-8 来源范围一次性把字形分配给字符 span。
 *
 * @param snapshot - 当前文字排版快照。
 * @returns 与 span 顺序一致的字形列表。
 */
const groupGlyphsBySpan = (snapshot: TextRenderSnapshot): GlyphInfo[][] => {
    const grouped = snapshot.spanRanges.map(() => [] as GlyphInfo[]);
    let firstCandidate = 0;
    [...snapshot.glyphInfos]
        .sort((first, second) => first.start - second.start)
        .forEach((glyph) => {
            while (snapshot.spanRanges[firstCandidate]?.end <= glyph.start) firstCandidate += 1;
            for (
                let index = firstCandidate;
                index < snapshot.spanRanges.length && snapshot.spanRanges[index].start < glyph.end;
                index += 1
            ) {
                const range = snapshot.spanRanges[index];
                if (glyph.start < range.end && glyph.end > range.start) grouped[index].push(glyph);
            }
        });
    return grouped;
};

/**
 * 为字形预计算视觉行和右侧 advance 边界。
 *
 * @param snapshot - 当前文字排版快照。
 * @param letterSpacing - 字形之间的字距。
 * @returns 字形到视觉行及右边界的映射。
 */
const indexGlyphGeometry = (
    snapshot: TextRenderSnapshot,
    letterSpacing: number,
): { lineIndexes: Map<GlyphInfo, number>; rightEdges: Map<GlyphInfo, number> } => {
    const glyphsByLine = snapshot.lineMetrics.map(() => [] as GlyphInfo[]);
    const lineIndexes = new Map<GlyphInfo, number>();
    const rightEdges = new Map<GlyphInfo, number>();
    snapshot.glyphInfos.forEach((glyph) => {
        const lineIndex = findLineIndex(snapshot.lineMetrics, glyph.y);
        if (lineIndex < 0) return;
        lineIndexes.set(glyph, lineIndex);
        glyphsByLine[lineIndex].push(glyph);
    });
    glyphsByLine.forEach((lineGlyphs, lineIndex) => {
        lineGlyphs.sort((first, second) => first.x - second.x);
        for (let index = 0; index < lineGlyphs.length; ) {
            let nextIndex = index + 1;
            while (lineGlyphs[nextIndex]?.x === lineGlyphs[index].x) nextIndex += 1;
            const nextX = lineGlyphs[nextIndex]?.x;
            const right = Math.max(
                lineGlyphs[index].x,
                nextX === undefined ? snapshot.lineMetrics[lineIndex].rightEdgeX : nextX - letterSpacing,
            );
            for (let current = index; current < nextIndex; current += 1) rightEdges.set(lineGlyphs[current], right);
            index = nextIndex;
        }
    });
    return { lineIndexes, rightEdges };
};

/**
 * 将四向边界绕中心旋转并转换为轴对齐边界。
 *
 * @param bounds - 旋转前四向边界。
 * @param angle - 顺时针角度。
 * @returns 旋转后的轴对齐边界。
 */
const rotateBounds = (bounds: Bounds, angle: number): Bounds => {
    if (!angle) return bounds;
    const centerX = (bounds.left + bounds.right) / 2;
    const centerY = (bounds.top + bounds.bottom) / 2;
    const radian = (angle * Math.PI) / 180;
    const cos = Math.cos(radian);
    const sin = Math.sin(radian);
    return transformBounds(bounds, (x, y) => ({
        x: centerX + (x - centerX) * cos - (y - centerY) * sin,
        y: centerY + (x - centerX) * sin + (y - centerY) * cos,
    }));
};

/**
 * 从 snapshot 派生一字符一项的花字几何片段。
 *
 * @param input - 当前 Text Render 输入。
 * @param snapshot - 当前文字排版快照。
 * @returns 与可见 span 顺序一致的片段；结构不完整时返回空数组。
 */
export const buildArtTextFragments = (input: TextRenderInput, snapshot: TextRenderSnapshot): ArtTextFragment[] => {
    if (input.spans.length === 0 || snapshot.spanRanges.length !== input.spans.length) return [];
    const glyphsBySpan = groupGlyphsBySpan(snapshot);
    const { lineIndexes, rightEdges } = indexGlyphGeometry(snapshot, input.letterSpacing);
    const fragments = snapshot.spanRanges.flatMap<ArtTextFragment>((_, spanIndex) => {
        const matched = glyphsBySpan[spanIndex];
        if (matched.length === 0) return [];
        let bounds: Bounds | null = null;
        matched.forEach((glyph) => {
            const line = snapshot.lineMetrics[lineIndexes.get(glyph) ?? -1];
            if (!line) return;
            bounds = unionBounds(bounds, {
                left: glyph.x,
                top: line.top,
                right: rightEdges.get(glyph) ?? line.rightEdgeX,
                bottom: line.bottom,
            });
        });
        const fragmentBounds = bounds as Bounds | null;
        if (!fragmentBounds) return [];
        const rotate = Number.isFinite(input.spans[spanIndex]?.rotate) ? (input.spans[spanIndex].rotate ?? 0) : 0;
        const glyphBounds = {
            left: fragmentBounds.left,
            top: fragmentBounds.top,
            right: fragmentBounds.right,
            bottom: fragmentBounds.bottom,
        };
        return [
            {
                spanIndex,
                visibleIndex: spanIndex,
                left: fragmentBounds.left,
                top: fragmentBounds.top,
                width: fragmentBounds.right - fragmentBounds.left,
                height: fragmentBounds.bottom - fragmentBounds.top,
                centerX: (fragmentBounds.left + fragmentBounds.right) / 2,
                centerY: (fragmentBounds.top + fragmentBounds.bottom) / 2,
                rotate,
                paintBounds: rotateBounds(glyphBounds, rotate),
            },
        ];
    });
    // 拖动宽度触发换行时，引擎可能省略换行处空格的字形，因此该空格不会生成花字片段。
    // 若仍要求片段数等于全部字符分段数，就会返回空数组，导致整层花字底块和字符旋转一起消失。
    // 这里只从预期数量中排除没有字形的空白分段，其余分段仍须完整；原 spanIndex 不变，避免后续素材循环错位。
    const expectedFragmentCount = input.spans.filter(
        (span, index) => span.text.trim().length > 0 || glyphsBySpan[index].length > 0,
    ).length;
    return fragments.length === expectedFragmentCount ? fragments : [];
};
